import { useEffect, useState } from "react";
import { Loader2, MessageCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { api, formatApiError, todayStr } from "../lib/api";
import { orderMessage, waLink } from "../lib/order";

const FIELDS = ["driver_id", "trip_date", "tour_id", "pax", "trip_name", "booking_platform", "pickup_time", "pickup_location", "guest_name", "guest_phone", "driver_note", "collect"];
const empty = (driverId, date) => ({ driver_id: driverId, trip_date: date || todayStr(), tour_id: "", pax: 1, trip_name: "", booking_platform: "", pickup_time: "", pickup_location: "", guest_name: "", guest_phone: "", driver_note: "", collect: 0 });

function Field({ label, testId, className = "", ...props }) {
  return <div className={`space-y-1.5 ${className}`}><Label>{label}</Label><Input {...props} data-testid={testId} /></div>;
}

function TourPicker({ tours, value, onChange, vendor }) {
  const list = [...tours].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="space-y-1.5">
      <Label>{vendor ? "Jenis Tour untuk Vendor" : "Jenis Tour untuk Driver"}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-11" data-testid="order-tour-select"><SelectValue placeholder="Pilih jenis tour" /></SelectTrigger>
        <SelectContent className="max-h-[55vh]" data-testid="order-tour-list">
          {list.map((t) => <SelectItem key={t.id} value={t.id} className="min-h-[44px]" data-testid={`order-tour-option-${t.id}`}>{t.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Picker({ label, value, onChange, items, placeholder, testId }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export function SentView({ order, driver }) {
  const msg = orderMessage(order, !!driver?.is_vendor);
  return (
    <div className="space-y-4" data-testid="order-sent-view">
      <pre className="whitespace-pre-wrap rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-xs text-slate-800 font-sans" data-testid="order-wa-preview">{msg}</pre>
      {!driver?.phone && <p className="flex items-center gap-2 text-xs text-amber-700"><AlertTriangle className="h-4 w-4" />No. HP driver belum diisi di Team Driver, pilih kontak manual di WhatsApp.</p>}
      <a href={waLink(driver?.phone, msg)} target="_blank" rel="noreferrer" className="flex h-11 items-center justify-center gap-2 rounded-lg bg-[#25D366] font-semibold text-white hover:brightness-95" data-testid="order-wa-btn">
        <MessageCircle className="h-5 w-5" /> Kirim ke WhatsApp {driver?.name}
      </a>
    </div>
  );
}

function useClash(form, orderId) {
  const [clash, setClash] = useState([]);
  const [off, setOff] = useState(null);
  useEffect(() => {
    if (!form.driver_id || !form.trip_date) { setOff(null); return setClash([]); }
    const params = { month: form.trip_date.slice(0, 7), driver_id: form.driver_id };
    api.get("/trips", { params })
      .then((r) => setClash(r.data.filter((t) => t.trip_date === form.trip_date && t.id !== orderId)))
      .catch(() => setClash([]));
    api.get("/dayoffs", { params })
      .then((r) => setOff(r.data.find((o) => o.date === form.trip_date) || null))
      .catch(() => setOff(null));
  }, [form.driver_id, form.trip_date, orderId]);
  return { clash, off };
}

export function OrderDialog({ open, onOpenChange, order, vendor = false, defaultDriverId = "", defaultDate = "", onSaved }) {
  const [form, setForm] = useState(empty(""));
  const [tours, setTours] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(null);
  const { clash, off } = useClash(form, order?.id);

  useEffect(() => {
    if (!open) return;
    setSent(null);
    const side = vendor ? "vendor" : "driver";
    api.get("/tours").then((r) => setTours(r.data.filter((t) => (t.active && ["both", side].includes(t.audience || "both")) || t.id === order?.tour_id)));
    api.get("/users").then((r) => setDrivers(r.data.filter((d) => (d.active && !!d.is_vendor === vendor) || d.id === order?.driver_id)));
    setForm(order ? { ...Object.fromEntries(FIELDS.map((k) => [k, order[k] ?? ""])), pax: Math.max(1, order.pax || 0) } : empty(defaultDriverId, defaultDate));
  }, [open, order, defaultDriverId, defaultDate, vendor]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.driver_id) return toast.error("Pilih driver");
    if (!form.tour_id) return toast.error("Pilih tour");
    const pax = Number(form.pax);
    if (!Number.isInteger(pax) || pax < 1) return toast.error("Total pax minimal 1 (angka bulat)");
    if (!form.driver_note.trim()) return toast.error("Isi catatan order (ini yang dikirim ke WhatsApp driver)");
    if (off) return toast.error("Driver libur di tanggal ini, pilih driver lain");
    setSaving(true);
    try {
      const payload = { ...form, pax: Number(form.pax) || 0, collect: Number(form.collect) || 0 };
      const { data } = order ? await api.put(`/orders/${order.id}`, payload) : await api.post("/orders", payload);
      toast.success(order ? "Order diperbarui" : "Order tersimpan di web");
      setSent(data);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="order-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{sent ? (vendor ? "Kirim Order ke Vendor" : "Kirim Order ke Driver") : order ? "Edit Order" : vendor ? "Kirim ke Vendor" : "Kirim ke Driver"}</DialogTitle>
          <DialogDescription>{sent ? "Order sudah tercatat. Tekan tombol untuk membuka WhatsApp." : vendor ? "Order tercatat otomatis, lalu kirim catatannya ke WhatsApp vendor." : "Order tercatat otomatis, lalu kirim detailnya ke WhatsApp driver."}</DialogDescription>
        </DialogHeader>
        {sent ? <SentView order={sent} driver={drivers.find((d) => d.id === sent.driver_id)} /> : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Picker label={vendor ? "Vendor" : "Driver"} value={form.driver_id} onChange={set("driver_id")} items={drivers} placeholder={vendor ? "Pilih vendor" : "Pilih driver"} testId="order-driver-select" />
              <Field label="Tanggal Trip" type="date" required value={form.trip_date} onChange={set("trip_date")} testId="order-date-input" />
              <div className="sm:col-span-2"><TourPicker tours={tours} value={form.tour_id} onChange={set("tour_id")} vendor={vendor} /></div>
              <Field label="Total Pax (data internal, tidak dikirim)" type="number" min={1} step={1} required value={form.pax} onChange={set("pax")} testId="order-pax-input" />
            </div>
            {off && (
              <p className="flex items-start gap-2 rounded-lg bg-slate-100 border border-slate-200 px-3 py-2 text-xs text-slate-700" data-testid="order-off-warning">
                <AlertTriangle className="h-4 w-4 shrink-0 text-slate-500" />
                Driver ini LIBUR/OFF di tanggal ini{off.note && ` (${off.note})`}. Pilih driver lain.
              </p>
            )}
            {clash.length > 0 && !vendor && (
              <p className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700" data-testid="order-clash-warning">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Driver ini sudah punya {clash.length} trip di tanggal ini: {clash.map((t) => t.tour_name).join(", ")}
              </p>
            )}
            <div className="space-y-1.5">
              <Label>Collect ke Tamu (Rp, opsional)</Label>
              <Input type="number" min={0} className="font-num" placeholder="0 = Voucher (tamu sudah bayar ke perusahaan)" value={form.collect || ""} onChange={set("collect")} data-testid="order-collect-input" />
              <p className="text-[11px] text-slate-500">Isi jika driver harus menagih uang ke tamu. Kosong = Voucher. Collect dipotong dari total bayar ke driver.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Catatan Order (hanya ini yang dikirim ke WhatsApp driver)</Label>
              <Textarea rows={10} className="min-h-[220px] text-sm" value={form.driver_note} onChange={set("driver_note")} placeholder={"Tempel detail order di sini, contoh:\nJam jemput, lokasi hotel, nama & HP tamu, jumlah tamu, platform booking, dll."} data-testid="order-note-input" />
            </div>
            <Button type="submit" disabled={saving} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="order-submit-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Simpan Order
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
